/**
 * Keeping the unlocked private key across a page refresh.
 *
 * Until now it lived in a module variable and nowhere else, so reloading the
 * page locked the keyring and left somebody signed in, looking at an album of
 * photographs they could not open, with no way out but to sign out and back in.
 * That is not a security property anybody was getting the benefit of; it is a
 * bug that reads as one.
 *
 * ── Why this is safe to store, when the password is not ──
 *
 * `unwrapPrivateKey` imports the key as **non-extractable**. WebCrypto will
 * happily hand that object to IndexedDB — structured clone preserves it — and
 * will never hand its bytes to script. Not to us, and not to anything injected
 * into the page: `exportKey` on it throws. So what is stored is a capability to
 * decrypt while this browser profile holds it, not a copy of the key.
 *
 * That is a genuinely different thing from writing the key, or the password,
 * into `localStorage`, and it is why this is worth doing and that would not be.
 *
 * ── What it does and does not change ──
 *
 * Nothing about the property this whole design exists for: the server still
 * never sees a key, and somebody holding the database, the bucket and the
 * application server still cannot open a single photograph.
 *
 * What it does change is the window on a *device* that is already compromised.
 * Script running in this origin could use the key while it runs — but it could
 * already do that during a session, by reading the module variable. The new
 * exposure is that the capability now outlives the tab, until sign-out clears
 * it. Given that the stated threat here is a compromised server rather than a
 * compromised phone, that is the right trade, and it is written down so it can
 * be revisited if that ever stops being true.
 */

const DB_NAME = "fc-keyring";
const STORE = "keys";
const RECORD = "private";
const VERSION = 1;

interface VaultRecord {
  /** Non-extractable. Usable, never readable. */
  privateKey: CryptoKey;
  publicKey: string;
  /**
   * Whose key this is.
   *
   * Checked on the way out, so a second person signing in on the same device
   * can never be handed the first one's key by a stale record.
   */
  userId: string;
}

/**
 * Every path here is allowed to fail quietly.
 *
 * IndexedDB is unavailable in some private-browsing modes, blocked by some
 * privacy settings, and throws rather than returning null when it is. None of
 * that should stop somebody signing in — it just means the keyring locks on
 * refresh, which is exactly where this started.
 */
function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, VERSION);
    } catch {
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    // A blocked upgrade would otherwise hang the promise for ever, and with it
    // whatever awaited it during start-up.
    request.onblocked = () => resolve(null);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
  const db = await open();
  if (!db) return null;

  return new Promise<T | null>((resolve) => {
    try {
      const transaction = db.transaction(STORE, mode);
      const request = work(transaction.objectStore(STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => resolve(null);
      transaction.onerror = () => resolve(null);
      transaction.oncomplete = () => db.close();
    } catch {
      db.close();
      resolve(null);
    }
  });
}

/** Remember the unlocked key for this device, for as long as the account stays signed in. */
export async function rememberPrivateKey(
  userId: string,
  publicKey: string,
  privateKey: CryptoKey,
): Promise<void> {
  const record: VaultRecord = { privateKey, publicKey, userId };
  await withStore("readwrite", (store) => store.put(record, RECORD));
}

/**
 * The key for this account, if this device has one.
 *
 * A record belonging to somebody else is not returned and not kept: two people
 * share a laptop far more often than they share a threat model.
 */
export async function recallPrivateKey(
  userId: string,
): Promise<{ publicKey: string; privateKey: CryptoKey } | null> {
  const record = await withStore<VaultRecord | undefined>("readonly", (store) =>
    store.get(RECORD),
  );
  if (!record?.privateKey) return null;

  if (record.userId !== userId) {
    await forgetPrivateKey();
    return null;
  }

  return { publicKey: record.publicKey, privateKey: record.privateKey };
}

/**
 * Forget it.
 *
 * Called on sign-out and on every failure to open. A key that outlived a
 * sign-out on a shared machine is the whole point of this being deliberate
 * rather than incidental.
 */
export async function forgetPrivateKey(): Promise<void> {
  await withStore("readwrite", (store) => store.delete(RECORD));
}
